import * as THREE from 'three';
import { getRarityCssColor as resolveRarityCssColor } from './materials.js';
import { RARITY_LABELS, type WeaponRarity } from './FpsWeaponRarity.js';

const VALID_RARITIES: readonly WeaponRarity[] = ['common', 'uncommon', 'rare', 'epic', 'legendary'];
const MOMENT_RARITIES = new Set<string>(['epic', 'legendary', 'mythic']);
const MYTHIC_COLOR = '#ffd700';

export type LootPresentationRarity = WeaponRarity | string | null | undefined;
type LootVisualRarity = WeaponRarity | 'mythic';

export type RarityPresentationHandle = {
  root: THREE.Group;
  beam: THREE.Mesh;
  ring: THREE.Mesh;
  rarity: LootVisualRarity;
};

export type PickupCardPayload = {
  name: string;
  rarity?: LootPresentationRarity;
  action?: string;
  meta?: string;
};

export type PickupCard = {
  show(payload: PickupCardPayload): void;
  hide(): void;
  dispose(): void;
};

export type RarityStinger = {
  show(payload: PickupCardPayload): boolean;
  dispose(): void;
};

export type LootEffectHandle = THREE.Group & { disposeEffect?: () => void };

export type DomEffectHandle = { dispose(): void };

type ArrivalPulseBit = {
  mesh: THREE.Mesh;
  velocity: THREE.Vector3;
};

function disposeObjectTree(root: THREE.Object3D): void {
  const geometries = new Set<THREE.BufferGeometry>();
  const materials = new Set<THREE.Material>();
  root.traverse((obj) => {
    if (obj instanceof THREE.Mesh) {
      geometries.add(obj.geometry);
      const mat = obj.material;
      if (Array.isArray(mat)) mat.forEach((m) => materials.add(m));
      else materials.add(mat);
    } else if (obj instanceof THREE.PointLight) {
      obj.dispose();
    }
    obj.onBeforeRender = () => {};
  });
  for (const geo of geometries) geo.dispose();
  for (const mat of materials) mat.dispose();
}

function normalizeRarity(rarity: LootPresentationRarity): LootVisualRarity {
  if (rarity === 'mythic') return 'mythic';
  return VALID_RARITIES.includes(rarity as WeaponRarity) ? (rarity as WeaponRarity) : 'common';
}

export function getRarityCssColor(rarity: LootPresentationRarity): string {
  const safeRarity = normalizeRarity(rarity);
  return safeRarity === 'mythic' ? MYTHIC_COLOR : resolveRarityCssColor(safeRarity);
}

export function getRarityThreeColor(rarity: LootPresentationRarity): number {
  return new THREE.Color(getRarityCssColor(rarity)).getHex();
}

export function getRarityLabel(rarity: LootPresentationRarity): string {
  const safeRarity = normalizeRarity(rarity);
  return safeRarity === 'mythic' ? 'Mythic' : RARITY_LABELS[safeRarity];
}

const LOOT_BEAM_NEAR_DIST = 6;
const LOOT_BEAM_FAR_DIST = 52;
const RARITY_BEAM_DISTANCE_KEEP: Record<LootVisualRarity, number> = {
  common: 0.4,
  uncommon: 0.48,
  rare: 0.56,
  epic: 0.66,
  legendary: 0.76,
  mythic: 0.84,
};

/** 0–1 multiplier: higher rarity keeps more beam intensity at range. */
export function computeLootBeamDistanceFactor(
  rarity: LootPresentationRarity,
  distance: number,
): number {
  const safeRarity = normalizeRarity(rarity);
  const keep = RARITY_BEAM_DISTANCE_KEEP[safeRarity];
  const dist = Math.max(0, distance);
  if (dist <= LOOT_BEAM_NEAR_DIST) return 1;
  const t = Math.min(1, (dist - LOOT_BEAM_NEAR_DIST) / (LOOT_BEAM_FAR_DIST - LOOT_BEAM_NEAR_DIST));
  return Math.max(keep, 1 - t * (1 - keep));
}

export function computeLootBeamOpacity(
  rarity: LootPresentationRarity,
  distance: number,
  pulse = 0.5,
): number {
  const profile = getRarityPresentationProfile(rarity);
  const base = profile.beamOpacity + pulse * profile.beamPulse;
  return Math.max(0.035, base * computeLootBeamDistanceFactor(rarity, distance));
}

export function applyLootBeamDistanceAlpha(root: THREE.Object3D, distance: number): void {
  const rarity = (root.userData.rarity as LootVisualRarity) ?? 'common';
  root.userData.beamDistanceFactor = computeLootBeamDistanceFactor(rarity, distance);
}

export function getRarityPresentationProfile(rarity: LootPresentationRarity): {
  beamHeight: number;
  beamOpacity: number;
  beamPulse: number;
  ringOpacity: number;
  ringPulse: number;
  haloOpacity: number;
  haloPulse: number;
  ringScale: number;
  particleCount: number;
  lightIntensity: number;
} {
  const safeRarity = normalizeRarity(rarity);
  if (safeRarity === 'mythic') {
    return { beamHeight: 12, beamOpacity: 0.32, beamPulse: 0.2, ringOpacity: 0.68, ringPulse: 0.3, haloOpacity: 0.2, haloPulse: 0.12, ringScale: 0.28, particleCount: 22, lightIntensity: 9 };
  }
  if (safeRarity === 'legendary') {
    return { beamHeight: 11, beamOpacity: 0.28, beamPulse: 0.18, ringOpacity: 0.6, ringPulse: 0.28, haloOpacity: 0.18, haloPulse: 0.11, ringScale: 0.24, particleCount: 22, lightIntensity: 8 };
  }
  if (safeRarity === 'epic') {
    return { beamHeight: 9.5, beamOpacity: 0.24, beamPulse: 0.16, ringOpacity: 0.54, ringPulse: 0.24, haloOpacity: 0.16, haloPulse: 0.1, ringScale: 0.2, particleCount: 20, lightIntensity: 8 };
  }
  if (safeRarity === 'rare') {
    return { beamHeight: 8, beamOpacity: 0.2, beamPulse: 0.12, ringOpacity: 0.48, ringPulse: 0.2, haloOpacity: 0.13, haloPulse: 0.08, ringScale: 0.16, particleCount: 16, lightIntensity: 6 };
  }
  if (safeRarity === 'uncommon') {
    return { beamHeight: 6.8, beamOpacity: 0.16, beamPulse: 0.09, ringOpacity: 0.38, ringPulse: 0.16, haloOpacity: 0.1, haloPulse: 0.06, ringScale: 0.12, particleCount: 14, lightIntensity: 5 };
  }
  return { beamHeight: 6.2, beamOpacity: 0.12, beamPulse: 0.07, ringOpacity: 0.3, ringPulse: 0.12, haloOpacity: 0.08, haloPulse: 0.05, ringScale: 0.1, particleCount: 12, lightIntensity: 4 };
}

export function attachRarityBeam(
  itemGroupOrMesh: THREE.Object3D,
  rarity: LootPresentationRarity,
): RarityPresentationHandle {
  const safeRarity = normalizeRarity(rarity);
  const color = getRarityThreeColor(safeRarity);
  const profile = getRarityPresentationProfile(safeRarity);
  const height = profile.beamHeight;
  const root = new THREE.Group();
  root.name = 'loot-rarity-presentation';
  root.userData.rarity = safeRarity;

  const beamMat = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: profile.beamOpacity,
    depthWrite: false,
    side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending,
  });
  const beam = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.055, height, 14, 1, true), beamMat);
  beam.position.y = height * 0.5;

  const ringMat = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: profile.ringOpacity,
    depthWrite: false,
    side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending,
  });
  const ring = new THREE.Mesh(new THREE.RingGeometry(0.34, 0.58, 36), ringMat);
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = -0.11;

  const haloMat = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: profile.haloOpacity,
    depthWrite: false,
    side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending,
  });
  const halo = new THREE.Mesh(new THREE.CircleGeometry(0.5, 36), haloMat);
  halo.rotation.x = -Math.PI / 2;
  halo.position.y = -0.115;

  root.add(halo, ring, beam);
  itemGroupOrMesh.add(root);

  const start = performance.now() * 0.001 + Math.random() * 2;
  beam.onBeforeRender = () => {
    const t = performance.now() * 0.001 - start;
    const pulse = 0.5 + Math.sin(t * 3.1) * 0.5;
    const distFactor = typeof root.userData.beamDistanceFactor === 'number'
      ? root.userData.beamDistanceFactor
      : 1;
    beam.rotation.y += 0.006;
    beamMat.opacity = (profile.beamOpacity + pulse * profile.beamPulse) * distFactor;
    ringMat.opacity = (profile.ringOpacity + pulse * profile.ringPulse) * distFactor;
    haloMat.opacity = (profile.haloOpacity + pulse * profile.haloPulse) * distFactor;
    const scale = 0.92 + pulse * profile.ringScale;
    ring.scale.setScalar(scale);
    halo.scale.setScalar(1.02 + pulse * 0.12);
  };

  return { root, beam, ring, rarity: safeRarity };
}

type BurstParticle = {
  mesh: THREE.Mesh;
  velocity: THREE.Vector3;
  startScale: number;
};

export function burstChest(
  scene: THREE.Scene,
  position: THREE.Vector3,
  rarity: LootPresentationRarity,
  _particles?: unknown,
): LootEffectHandle {
  const safeRarity = normalizeRarity(rarity);
  const color = getRarityThreeColor(safeRarity);
  const profile = getRarityPresentationProfile(safeRarity);
  const burst = new THREE.Group() as LootEffectHandle;
  burst.position.copy(position);
  scene.add(burst);

  const ringMat = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 0.68,
    depthWrite: false,
    side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending,
  });
  const ring = new THREE.Mesh(new THREE.RingGeometry(0.25, 0.36, 42), ringMat);
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = 0.02;
  burst.add(ring);

  const glowMat = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 0.26,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const glow = new THREE.Mesh(new THREE.SphereGeometry(0.5, 18, 10), glowMat);
  glow.position.y = 0.32;
  burst.add(glow);

  const light = new THREE.PointLight(color, profile.lightIntensity, 9);
  light.position.y = 0.5;
  burst.add(light);

  const sparkGeo = new THREE.OctahedronGeometry(0.055, 0);
  const bits: BurstParticle[] = [];
  const count = profile.particleCount;
  for (let i = 0; i < count; i++) {
    const mat = new THREE.MeshBasicMaterial({
      color: i % 4 === 0 ? 0xffffff : color,
      transparent: true,
      opacity: 0.94,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const mesh = new THREE.Mesh(sparkGeo, mat);
    mesh.position.set((Math.random() - 0.5) * 0.36, 0.18 + Math.random() * 0.24, (Math.random() - 0.5) * 0.36);
    mesh.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI);
    const speed = 1.1 + Math.random() * 1.4;
    const velocity = new THREE.Vector3((Math.random() - 0.5) * speed, 1.4 + Math.random() * 1.8, (Math.random() - 0.5) * speed);
    const startScale = 0.65 + Math.random() * 0.8;
    mesh.scale.setScalar(startScale);
    bits.push({ mesh, velocity, startScale });
    burst.add(mesh);
  }

  const start = performance.now() * 0.001;
  let last = start;
  let disposed = false;
  burst.disposeEffect = () => {
    if (disposed) return;
    disposed = true;
    burst.removeFromParent();
    disposeObjectTree(burst);
  };
  ring.onBeforeRender = () => {
    if (disposed) return;
    const now = performance.now() * 0.001;
    const dt = Math.min(0.05, now - last);
    last = now;
    const t = now - start;
    const u = Math.min(1, t / 0.72);
    ring.scale.setScalar(1 + u * 3.6);
    ringMat.opacity = 0.68 * (1 - u) * (1 - u * 0.25);
    glow.scale.setScalar(1 + u * 1.3);
    glowMat.opacity = 0.26 * (1 - u);
    light.intensity = Math.max(0, light.intensity * (1 - dt * 4.8));
    for (const bit of bits) {
      bit.mesh.position.addScaledVector(bit.velocity, dt);
      bit.velocity.y -= dt * 4.2;
      bit.velocity.multiplyScalar(1 - dt * 1.8);
      bit.mesh.rotation.x += dt * 7;
      bit.mesh.rotation.y += dt * 5;
      bit.mesh.scale.setScalar(bit.startScale * (1 - u * 0.55));
      (bit.mesh.material as THREE.MeshBasicMaterial).opacity = 0.94 * (1 - u);
    }
    if (u >= 1) {
      burst.disposeEffect?.();
    }
  };

  return burst;
}

export function spawnRareLootArrivalPulse(
  scene: THREE.Scene,
  position: THREE.Vector3,
  rarity: LootPresentationRarity,
): LootEffectHandle | null {
  const safeRarity = normalizeRarity(rarity);
  if (!MOMENT_RARITIES.has(safeRarity) && safeRarity !== 'rare') return null;

  const color = getRarityThreeColor(safeRarity);
  const profile = getRarityPresentationProfile(safeRarity);
  const root = new THREE.Group() as LootEffectHandle;
  root.name = 'loot-arrival-pulse';
  root.position.copy(position);
  root.position.y += 0.08;
  scene.add(root);

  const ringMat = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 0.72,
    depthWrite: false,
    side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending,
  });
  const ring = new THREE.Mesh(new THREE.RingGeometry(0.18, 0.28, 48), ringMat);
  ring.rotation.x = -Math.PI / 2;
  root.add(ring);

  const beamMat = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: Math.min(0.34, profile.beamOpacity + 0.12),
    depthWrite: false,
    side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending,
  });
  const beam = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.02, 3.4, 16, 1, true), beamMat);
  beam.position.y = 1.7;
  root.add(beam);

  const light = new THREE.PointLight(color, Math.min(7, profile.lightIntensity), 7);
  light.position.y = 0.7;
  root.add(light);

  const sparkGeo = new THREE.TetrahedronGeometry(0.045, 0);
  const sparks: ArrivalPulseBit[] = [];
  const count = Math.min(12, profile.particleCount);
  for (let i = 0; i < count; i++) {
    const mat = new THREE.MeshBasicMaterial({
      color: i % 3 === 0 ? 0xffffff : color,
      transparent: true,
      opacity: 0.86,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const mesh = new THREE.Mesh(sparkGeo, mat);
    const a = (i / count) * Math.PI * 2;
    const speed = 0.55 + (i % 4) * 0.08;
    mesh.position.set(Math.cos(a) * 0.16, 0.1 + (i % 3) * 0.03, Math.sin(a) * 0.16);
    const velocity = new THREE.Vector3(Math.cos(a) * speed, 0.38 + (i % 3) * 0.08, Math.sin(a) * speed);
    sparks.push({ mesh, velocity });
    root.add(mesh);
  }

  const start = performance.now() * 0.001;
  let last = start;
  let disposed = false;
  root.disposeEffect = () => {
    if (disposed) return;
    disposed = true;
    root.removeFromParent();
    disposeObjectTree(root);
  };
  ring.onBeforeRender = () => {
    if (disposed) return;
    const now = performance.now() * 0.001;
    const dt = Math.min(0.05, now - last);
    last = now;
    const t = now - start;
    const u = Math.min(1, t / 0.62);
    const settle = 1 - (1 - u) * (1 - u);
    ring.scale.setScalar(0.75 + settle * 2.9);
    ringMat.opacity = 0.72 * (1 - u);
    beam.scale.y = Math.max(0.08, 1 - settle * 0.62);
    beamMat.opacity = 0.34 * (1 - u * 0.9);
    light.intensity = Math.max(0, light.intensity * (1 - dt * 4.2));
    for (const spark of sparks) {
      spark.mesh.position.addScaledVector(spark.velocity, dt);
      spark.velocity.y -= dt * 1.8;
      spark.velocity.multiplyScalar(1 - dt * 1.25);
      spark.mesh.rotation.x += dt * 6;
      spark.mesh.rotation.y += dt * 4;
      (spark.mesh.material as THREE.MeshBasicMaterial).opacity = 0.86 * (1 - u);
    }
    if (u >= 1) {
      root.disposeEffect?.();
    }
  };

  return root;
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[ch]!);
}

export function createPickupCard(root: HTMLElement): PickupCard {
  const el = document.createElement('div');
  let hideTimer = 0;
  let disposed = false;
  el.style.cssText = [
    'position:fixed',
    'left:50%',
    'bottom:calc(24% + env(safe-area-inset-bottom, 0px) * 0.5)',
    'transform:translate(-50%,10px) scale(0.98)',
    'z-index:92',
    'min-width:min(300px,78vw)',
    'max-width:min(420px,calc(100vw - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px) - 24px))',
    'padding:clamp(8px,1.8vw,11px) clamp(10px,2.4vw,14px) clamp(9px,1.9vw,12px)',
    'border-radius:14px',
    'background:linear-gradient(135deg,rgba(8,12,24,0.92),rgba(18,20,38,0.76))',
    'border:2px solid rgba(255,255,255,0.16)',
    'box-shadow:0 18px 40px rgba(0,0,0,0.38),inset 0 1px 0 rgba(255,255,255,0.1)',
    'font-family:"Trebuchet MS","Segoe UI",system-ui,sans-serif',
    'color:#fff',
    'pointer-events:none',
    'display:none',
    'opacity:0',
    'text-align:left',
    'transition:opacity 140ms ease,transform 140ms ease',
  ].join(';');
  root.appendChild(el);

  return {
    show(payload: PickupCardPayload): void {
      if (disposed) return;
      const rarity = normalizeRarity(payload.rarity);
      const color = getRarityCssColor(rarity);
      const label = getRarityLabel(rarity);
      el.style.borderColor = `${color}cc`;
      el.style.boxShadow = `0 18px 42px rgba(0,0,0,0.42),0 0 30px ${color}33,inset 0 1px 0 rgba(255,255,255,0.12)`;
      el.innerHTML = [
        `<div style="font:900 clamp(9px,2vw,11px) system-ui,sans-serif;letter-spacing:0.16em;text-transform:uppercase;color:${color};text-shadow:0 0 12px ${color}88">${label}</div>`,
        `<div style="margin-top:2px;font:900 clamp(14px,3.4vw,18px) 'Trebuchet MS','Segoe UI',system-ui,sans-serif;letter-spacing:0.03em;color:#fff">${escapeHtml(payload.name)}</div>`,
        payload.action ? `<div style="margin-top:5px;font:900 clamp(10px,2.5vw,12px) system-ui,sans-serif;color:${color};letter-spacing:0.14em;text-transform:uppercase">${escapeHtml(payload.action)}</div>` : '',
        payload.meta ? `<div style="margin-top:4px;font:800 clamp(10px,2.4vw,12px) system-ui,sans-serif;color:#c9d4e8;letter-spacing:0.04em">${escapeHtml(payload.meta)}</div>` : '',
      ].join('');
      el.style.display = 'block';
      requestAnimationFrame(() => {
        if (disposed) return;
        el.style.opacity = '1';
        el.style.transform = 'translate(-50%,0) scale(1)';
      });
    },
    hide(): void {
      if (disposed) return;
      window.clearTimeout(hideTimer);
      el.style.opacity = '0';
      el.style.transform = 'translate(-50%,10px) scale(0.98)';
      hideTimer = window.setTimeout(() => {
        if (disposed) return;
        if (el.style.opacity === '0') el.style.display = 'none';
      }, 160);
    },
    dispose(): void {
      disposed = true;
      window.clearTimeout(hideTimer);
      el.remove();
    },
  };
}

export function createRarityStinger(root: HTMLElement): RarityStinger {
  const el = document.createElement('div');
  let hideTimer = 0;
  let hideCompleteTimer = 0;
  let nextAllowed = 0;
  let disposed = false;
  el.style.cssText = [
    'position:fixed',
    'left:50%',
    'top:calc(14% + env(safe-area-inset-top, 0px) * 0.5)',
    'transform:translate(-50%,-18px) scale(0.96)',
    'z-index:96',
    'min-width:min(260px,76vw)',
    'max-width:calc(100vw - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px) - 24px)',
    'padding:clamp(8px,1.8vw,10px) clamp(12px,3vw,18px) clamp(9px,2vw,12px)',
    'border-radius:16px',
    'background:linear-gradient(135deg,rgba(10,8,20,0.9),rgba(22,18,38,0.72))',
    'border:2px solid rgba(255,255,255,0.2)',
    'box-shadow:0 18px 44px rgba(0,0,0,0.44),inset 0 1px 0 rgba(255,255,255,0.14)',
    'font-family:"Trebuchet MS","Segoe UI",system-ui,sans-serif',
    'color:#fff',
    'pointer-events:none',
    'display:none',
    'opacity:0',
    'text-align:center',
    'transition:opacity 120ms ease,transform 180ms cubic-bezier(.16,.9,.32,1)',
  ].join(';');
  root.appendChild(el);

  return {
    show(payload: PickupCardPayload): boolean {
      if (disposed) return false;
      const rarity = normalizeRarity(payload.rarity);
      if (!MOMENT_RARITIES.has(rarity)) return false;
      const now = performance.now();
      if (now < nextAllowed) return false;
      nextAllowed = now + 900;
      const color = getRarityCssColor(rarity);
      const label = getRarityLabel(rarity);
      window.clearTimeout(hideTimer);
      window.clearTimeout(hideCompleteTimer);
      el.style.borderColor = `${color}e6`;
      el.style.boxShadow = `0 18px 46px rgba(0,0,0,0.46),0 0 36px ${color}66,inset 0 1px 0 rgba(255,255,255,0.16)`;
      el.innerHTML = [
        `<div style="font:1000 clamp(9px,2vw,11px) system-ui,sans-serif;letter-spacing:0.22em;text-transform:uppercase;color:${color};text-shadow:0 0 14px ${color}">${label} Find</div>`,
        `<div style="margin-top:3px;font:1000 clamp(15px,3.8vw,20px) 'Trebuchet MS','Segoe UI',system-ui,sans-serif;letter-spacing:0.04em;color:#fff;text-shadow:0 2px 14px rgba(0,0,0,0.78)">${escapeHtml(payload.name)}</div>`,
        payload.action ? `<div style="margin-top:5px;font:900 clamp(10px,2.5vw,12px) system-ui,sans-serif;letter-spacing:0.16em;text-transform:uppercase;color:${color}">${escapeHtml(payload.action)}</div>` : '',
      ].join('');
      el.style.display = 'block';
      requestAnimationFrame(() => {
        if (disposed) return;
        el.style.opacity = '1';
        el.style.transform = 'translate(-50%,0) scale(1)';
      });
      hideTimer = window.setTimeout(() => {
        if (disposed) return;
        el.style.opacity = '0';
        el.style.transform = 'translate(-50%,-18px) scale(0.96)';
        hideCompleteTimer = window.setTimeout(() => {
          if (!disposed && el.style.opacity === '0') el.style.display = 'none';
        }, 180);
      }, 980);
      return true;
    },
    dispose(): void {
      disposed = true;
      window.clearTimeout(hideTimer);
      window.clearTimeout(hideCompleteTimer);
      el.remove();
    },
  };
}

export function spawnPickupFlyChip(root: HTMLElement, text: string, color = '#f1c40f'): DomEffectHandle {
  const chip = document.createElement('div');
  let disposed = false;
  let timer = 0;
  let frame = 0;
  chip.textContent = text;
  chip.style.cssText = [
    'position:fixed',
    'left:50%',
    'top:62%',
    'transform:translate(-50%,-50%) scale(1)',
    'z-index:93',
    'padding:7px 13px',
    'border-radius:999px',
    `border:2px solid ${color}cc`,
    'background:rgba(8,12,24,0.82)',
    `color:${color}`,
    'font:900 13px system-ui,sans-serif',
    'letter-spacing:0.08em',
    'text-shadow:0 2px 8px rgba(0,0,0,0.72)',
    'pointer-events:none',
    'opacity:1',
    'transition:transform 720ms cubic-bezier(.16,.84,.28,1),opacity 720ms ease',
  ].join(';');
  root.appendChild(chip);
  frame = requestAnimationFrame(() => {
    if (disposed) return;
    chip.style.transform = 'translate(calc(50vw - 150px),calc(-50vh + 70px)) scale(0.72)';
    chip.style.opacity = '0';
  });
  timer = window.setTimeout(() => {
    disposed = true;
    chip.remove();
  }, 780);
  return {
    dispose(): void {
      if (disposed) return;
      disposed = true;
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
      chip.remove();
    },
  };
}
